import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { TrackStyles } from '../../../../../configuration/model/TrackStyles';
import { LayerTypeFilter } from '../../../../../configuration/model/LayerTypeFilter';
import { TrackStylesService } from '../../../../../configuration/track-styles.service';
import { ConfigurationService } from '../../../../../configuration/configuration.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { StatusLayerType } from '../../../../../configuration/settings.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { CardComponent } from '../../../../../shared/components/card/card.component';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatIcon } from '@angular/material/icon';
import { CardContentDirective, CardContentRawDirective } from '../../../../../shared/components/card/card-content.directive';
import {
  MapThemeEditorComponent
} from '../../../../../shared/components/map-theme-editor/map-theme-editor.component';
import { RowWithSelectComponent } from '../../row-with-select/row-with-select.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NamedId } from '../../../../../shared/models/NamedId';
import { DatePipe } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { ConfigurationModel } from '../../../../../shared/models/configuration.model';

export enum MapThemeType {
  CURRENCY = 'time-map',
  COVERAGE = 'pass-count-map',
  GPS_TRACKS = 'gps-tracks',
  ASSIGNMENT_STATUS = 'assignment-status',
  ROUTE_STATUS = 'route-status',
}

@Component({
  selector: 'app-map-theme-settings',
  imports: [
    CardComponent,
    MatNavList,
    MatListItem,
    MatIcon,
    CardContentRawDirective,
    CardContentDirective,
    MapThemeEditorComponent,
    RowWithSelectComponent,
    ReactiveFormsModule,
    DatePipe,
    MatIconButton,
    MatButton,
  ],
  templateUrl: './map-theme-settings.component.html',
  styleUrl: './map-theme-settings.component.scss',
})
export class MapThemeSettingsComponent implements OnInit, OnDestroy {
  @Output() editing = new EventEmitter<boolean>();

  editingThemeType: MapThemeType | undefined;

  configuration: ConfigurationModel;
  private trackStyles: TrackStyles;
  themeStyle: LayerTypeFilter;
  private stylesDirty: boolean;
  formGroup: FormGroup = this.fb.group<any>({
    trackDurationHours: this.fb.control(null),
  });
  trackDurationHoursOptions: NamedId[];

  readonly MapThemeType = MapThemeType;
  private defaultStylesSubscription: Subscription;
  private tenantConfigurationSubscription: Subscription;

  constructor(
    private readonly trackStylesService: TrackStylesService,
    private readonly configurationService: ConfigurationService,
    private readonly fb: FormBuilder,
    private readonly toast: ToastService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit() {
    this.defaultStylesSubscription = this.configurationService.trackStyles$.subscribe(trackStyles => {
      this.reloadLocalTrackStyles(trackStyles);
    });

    // initialize Track Duration form
    this.tenantConfigurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(tenantConfiguration => {
      if (tenantConfiguration) {
        this.configuration = tenantConfiguration;
        // array: 12...maxTrackHistoryHours
        this.trackDurationHoursOptions = Array.from({ length: tenantConfiguration.maxTrackHistoryHours - 11 }, (_, i) => {
          return {
            id: i + 12,
            name: i + 12 + ' hours',
          } as NamedId;
        });

        if (!this.formGroup.controls['trackDurationHours'].value) {
          // only initialization
          this.formGroup.setValue({ trackDurationHours: tenantConfiguration.lookBackPeriod });
        }
      }
    });

    this.formGroup.controls['trackDurationHours'].valueChanges.subscribe(value => {
      if (value) {
        this.configurationService
          .updateTrackDurationHours(value)
          .then(() => {
            this.toast.short('Track Duration value updated.');
            this.configurationService.refreshConfiguration();
          })
          .catch(error => {
            console.error(error);
            this.toast.longer('Track Duration value could not be updated.');
          });
      }
    });
  }

  ngOnDestroy() {
    this.defaultStylesSubscription?.unsubscribe();
    this.tenantConfigurationSubscription?.unsubscribe();
  }

  private reloadLocalTrackStyles(trackStyles: TrackStyles) {
    this.trackStyles = JSON.parse(JSON.stringify(trackStyles));
    if (this.editingThemeType) {
      this.updateThemeStyleFromTrackStyles(this.editingThemeType);
    }
    this.stylesDirty = false;
  }

  getCardTitle() {
    switch (this.editingThemeType) {
      case MapThemeType.CURRENCY:
        return 'Time Map Theme';
      case MapThemeType.COVERAGE:
        return 'Pass Count Map Theme';
      case MapThemeType.GPS_TRACKS:
        return 'GPS Tracks';
      case MapThemeType.ASSIGNMENT_STATUS:
        return 'Assignment Status Theme';
      case MapThemeType.ROUTE_STATUS:
        return 'Route Status Theme';
      default:
        return '';
    }
  }

  themeStyleChanged(themeStyle: LayerTypeFilter) {
    this.stylesDirty = true;
    switch (this.editingThemeType) {
      case MapThemeType.CURRENCY:
        // validation
        if (!themeStyle || themeStyle.filterName !== StatusLayerType.CURRENCY || themeStyle.measurementUnits.length !== 5) {
          console.warn('Something wrong with currency style!');
          return;
        }
        this.trackStyles.roadStatusCurrencyLevel1.color = themeStyle.measurementUnits[0].color;
        this.trackStyles.roadStatusCurrencyLevel1.dasharray = themeStyle.measurementUnits[0].dashArray;
        this.trackStyles.roadStatusCurrencyLevel2.color = themeStyle.measurementUnits[1].color;
        this.trackStyles.roadStatusCurrencyLevel2.dasharray = themeStyle.measurementUnits[1].dashArray;
        this.trackStyles.roadStatusCurrencyLevel3.color = themeStyle.measurementUnits[2].color;
        this.trackStyles.roadStatusCurrencyLevel3.dasharray = themeStyle.measurementUnits[2].dashArray;
        this.trackStyles.roadStatusCurrencyLevel4.color = themeStyle.measurementUnits[3].color;
        this.trackStyles.roadStatusCurrencyLevel4.dasharray = themeStyle.measurementUnits[3].dashArray;
        this.trackStyles.roadStatusCurrencyLevel5.color = themeStyle.measurementUnits[4].color;
        this.trackStyles.roadStatusCurrencyLevel5.dasharray = themeStyle.measurementUnits[4].dashArray;
        break;
      case MapThemeType.COVERAGE:
        // validation
        if (!themeStyle || themeStyle.filterName !== StatusLayerType.COVERAGE || themeStyle.measurementUnits.length !== 5) {
          console.warn('Something wrong with coverage style!');
          return;
        }
        this.trackStyles.roadStatusCoverageLevel1.color = themeStyle.measurementUnits[0].color;
        this.trackStyles.roadStatusCoverageLevel1.dasharray = themeStyle.measurementUnits[0].dashArray;
        this.trackStyles.roadStatusCoverageLevel2.color = themeStyle.measurementUnits[1].color;
        this.trackStyles.roadStatusCoverageLevel2.dasharray = themeStyle.measurementUnits[1].dashArray;
        this.trackStyles.roadStatusCoverageLevel3.color = themeStyle.measurementUnits[2].color;
        this.trackStyles.roadStatusCoverageLevel3.dasharray = themeStyle.measurementUnits[2].dashArray;
        this.trackStyles.roadStatusCoverageLevel4.color = themeStyle.measurementUnits[3].color;
        this.trackStyles.roadStatusCoverageLevel4.dasharray = themeStyle.measurementUnits[3].dashArray;
        this.trackStyles.roadStatusCoverageLevel5.color = themeStyle.measurementUnits[4].color;
        this.trackStyles.roadStatusCoverageLevel5.dasharray = themeStyle.measurementUnits[4].dashArray;
        break;
      case MapThemeType.GPS_TRACKS:
        // validation
        const active = themeStyle.measurementUnits.find(i => i.number === 'Active');
        const recent = themeStyle.measurementUnits.find(i => i.number === 'Recent');
        if (!themeStyle || themeStyle.filterName !== StatusLayerType.GPS_TRACKS || !active || !recent) {
          console.warn('Something wrong with gps tracks style!');
          return;
        }
        this.trackStyles.liveMapPlowLive.color = active.color;
        this.trackStyles.liveMapPlowLive.dasharray = active.dashArray;
        this.trackStyles.liveMapPlowInactive.color = recent.color;
        this.trackStyles.liveMapPlowInactive.dasharray = recent.dashArray;
        break;
      case MapThemeType.ASSIGNMENT_STATUS:
        // validation
        const serviced = themeStyle.measurementUnits.find(i => i.number === 'Serviced');
        const inProgress = themeStyle.measurementUnits.find(i => i.number === 'In Progress');
        const notServiced = themeStyle.measurementUnits.find(i => i.number === 'Not Serviced');
        if (!themeStyle || themeStyle.filterName !== StatusLayerType.ASSIGNMENT_STATUS || !serviced || !inProgress || !notServiced) {
          console.warn('Something wrong with assignment status style!');
          return;
        }
        this.trackStyles.assignmentStatusServiced.color = serviced.color;
        this.trackStyles.assignmentStatusServiced.dasharray = serviced.dashArray;
        this.trackStyles.assignmentStatusInProgress.color = inProgress.color;
        this.trackStyles.assignmentStatusInProgress.dasharray = inProgress.dashArray;
        this.trackStyles.assignmentStatusNotServiced.color = notServiced.color;
        this.trackStyles.assignmentStatusNotServiced.dasharray = notServiced.dashArray;
        break;
      case MapThemeType.ROUTE_STATUS:
        // validation
        const done = themeStyle.measurementUnits.find(i => i.number === 'Serviced');
        const level1 = themeStyle.measurementUnits.find(i => i.number === 'In Progress');
        const level2 = themeStyle.measurementUnits.find(i => i.number === 'Not Serviced');
        const level3 = themeStyle.measurementUnits.find(i => i.number === 'Not Serviced');
        if (!themeStyle || themeStyle.filterName !== StatusLayerType.ROUTE_STATUS || !done || !level1 || !level2 || !level3) {
          console.warn('Something wrong with route status style!');
          return;
        }
        this.trackStyles.routeStatusNotDone.color = done.color;
        this.trackStyles.routeStatusNotDone.dasharray = done.dashArray;
        this.trackStyles.routeStatusLevel1.color = level1.color;
        this.trackStyles.routeStatusLevel1.dasharray = level1.dashArray;
        this.trackStyles.routeStatusLevel2.color = level2.color;
        this.trackStyles.routeStatusLevel2.dasharray = level2.dashArray;
        this.trackStyles.routeStatusLevel3.color = level3.color;
        this.trackStyles.routeStatusLevel3.dasharray = level3.dashArray;
        break;
      default:
        console.warn('Not implemented themeStyleChanged()!');
        break;
    }
  }

  isButtonVisibleSave(): boolean {
    return this.stylesDirty;
  }

  async buttonClickedSave() {
    switch (this.editingThemeType) {
      case MapThemeType.CURRENCY:
        try {
          await TrackStyles.saveCurrency(this.trackStyles, this.trackStylesService);
          this.toast.longer('Time Map Theme customization have been saved');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while saving currency styling :: error = '${error}'`);
          this.toast.longer('Error while saving time map theme customization');
        }
        break;
      case MapThemeType.COVERAGE:
        try {
          await TrackStyles.saveCoverage(this.trackStyles, this.trackStylesService);
          this.toast.longer('Pass Count Map Theme customization have been saved');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while saving coverage styling :: error = '${error}'`);
          this.toast.longer('Error while saving pass count map theme customization');
        }
        break;
      case MapThemeType.GPS_TRACKS:
        try {
          await TrackStyles.saveGpsTracks(this.trackStyles, this.trackStylesService);
          this.toast.longer('GPS Tracks Theme customization have been saved');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while saving gps tracks styling :: error = '${error}'`);
          this.toast.longer('Error while saving gps tracks theme customization');
        }
        break;
      case MapThemeType.ASSIGNMENT_STATUS:
        try {
          await TrackStyles.saveAssignmentStatus(this.trackStyles, this.trackStylesService);
          this.toast.longer('Assignment Status Theme customization have been saved');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while saving assignment status styling :: error = '${error}'`);
          this.toast.longer('Error while saving assignment status theme customization');
        }
        break;
      case MapThemeType.ROUTE_STATUS:
        try {
          await TrackStyles.saveRouteStatus(this.trackStyles, this.trackStylesService);
          this.toast.longer('Route Status Theme customization have been saved');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while saving route status styling :: error = '${error}'`);
          this.toast.longer('Error while saving route status theme customization');
        }
        break;
      default:
        console.warn('Not saving! Missing case?');
        break;
    }
  }

  async buttonClickedReset() {
    switch (this.editingThemeType) {
      case MapThemeType.CURRENCY:
        try {
          await TrackStyles.resetCurrency(this.trackStyles, this.trackStylesService);
          this.toast.longer('Time Map Theme has been reset to default');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while resetting currency styling :: error = '${error}'`);
          this.toast.longer('Error while resetting time map theme customization');
        }
        break;
      case MapThemeType.COVERAGE:
        try {
          await TrackStyles.resetCoverage(this.trackStyles, this.trackStylesService);
          this.toast.longer('Pass Count Map Theme has been reset to default');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while resetting coverage styling :: error = '${error}'`);
          this.toast.longer('Error while resetting pass count map theme customization');
        }
        break;
      case MapThemeType.GPS_TRACKS:
        try {
          await TrackStyles.resetGpsTracks(this.trackStyles, this.trackStylesService);
          this.toast.longer('GPS Tracks Theme has been reset to default');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while resetting gps tracks styling :: error = '${error}'`);
          this.toast.longer('Error while resetting gps tracks theme customization');
        }
        break;
      case MapThemeType.ASSIGNMENT_STATUS:
        try {
          await TrackStyles.resetAssignmentStatus(this.trackStyles, this.trackStylesService);
          this.toast.longer('Assignment Status Theme has been reset to default');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while resetting assignment status styling :: error = '${error}'`);
          this.toast.longer('Error while resetting assignment status theme customization');
        }
        break;
      case MapThemeType.ROUTE_STATUS:
        try {
          await TrackStyles.resetRouteStatus(this.trackStyles, this.trackStylesService);
          this.toast.longer('Route Status Theme has been reset to default');
          this.configurationService.refreshTrackStyles();
        } catch (error) {
          console.error(`error while resetting route status styling :: error = '${error}'`);
          this.toast.longer('Error while resetting route status theme customization');
        }
        break;
      default:
        console.warn('Not resetting! Missing case?');
        break;
    }
  }

  onEditClick(themeType: MapThemeType) {
    this.updateThemeStyleFromTrackStyles(themeType);
    this.editingThemeType = themeType;
    this.editing.emit(true);
  }

  private updateThemeStyleFromTrackStyles(themeType: MapThemeType): void {
    switch (themeType) {
      case MapThemeType.CURRENCY:
        this.themeStyle = LayerTypeFilter.currencyLayerTypeFilter(this.trackStyles, 1);
        break;
      case MapThemeType.COVERAGE:
        this.themeStyle = LayerTypeFilter.coverageLayerTypeFilter(this.trackStyles);
        break;
      case MapThemeType.GPS_TRACKS:
        this.themeStyle = LayerTypeFilter.gpsTracksLayerTypeFilter(this.trackStyles);
        break;
      case MapThemeType.ASSIGNMENT_STATUS:
        this.themeStyle = LayerTypeFilter.assignmentStatusLayerTypeFilter(this.trackStyles);
        break;
      case MapThemeType.ROUTE_STATUS:
        this.themeStyle = LayerTypeFilter.routeStatusLayerTypeFilter(this.trackStyles);
        break;
      default:
        console.warn('Not updating theme style! Missing case?');
        break;
    }
  }

  clearMapContent() {
    this.configurationService.resetContent(new Date()).then(() => {
      this.configurationService.refreshConfiguration();
    });
  }

  removeMapContentRestriction() {
    this.configurationService.resetContent(null).then(() => {
      this.configurationService.refreshConfiguration();
    });
  }

  backToList() {
    let confirmationPromise = Promise.resolve(true);
    if (this.stylesDirty) {
      confirmationPromise = firstValueFrom(
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Confirmation',
              text: 'Current theme changes are not saved. Do you want to discard changes?',
            },
          })
          .afterClosed()
      );
    }
    confirmationPromise.then(async result => {
      if (!result) {
        // cancel
        return null;
      }
      // confirmed leaving to list
      this.editing.emit(false);
      this.themeStyle = null;
      this.editingThemeType = null;

      // not saving changes - reload styles from configuration service
      if (this.stylesDirty) {
        this.reloadLocalTrackStyles(this.configurationService.trackStyles$.getValue());
      }
    });
  }
}
